import { trpc } from '@/lib/trpc';
import { Card, CardContent } from '@/components/ui/Card';

interface ComparativoViewProps {
  eventoId: string;
}

export function ComparativoView({ eventoId }: ComparativoViewProps) {
  const { data: comparativo, isLoading } = trpc.previsaoOrcamentaria.getComparativo.useQuery({ eventoId });

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-600 border-t-transparent" />
      </div>
    );
  }

  if (!comparativo) return null;

  const diferencaSaldo = comparativo.saldoRealizado - comparativo.saldoPrevisto;

  return (
    <div className="space-y-6">
      {/* Receitas */}
      <Card className="border border-gray-200">
        <CardContent className="p-0">
          <div className="bg-green-50 px-4 py-3 border-b border-green-200">
            <h3 className="font-semibold text-green-800">Receitas - Previsto vs Realizado</h3>
          </div>
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-100">
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 w-10"></th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Conta</th>
                <th className="px-4 py-2 text-right text-xs font-medium text-gray-500">Previsto</th>
                <th className="px-4 py-2 text-right text-xs font-medium text-gray-500">Realizado</th>
                <th className="px-4 py-2 text-right text-xs font-medium text-gray-500">Diferença</th>
              </tr>
            </thead>
            <tbody>
              {comparativo.receitas.map((r) => (
                <tr key={r.codigo} className="border-b border-gray-50">
                  <td className="px-4 py-2 text-sm text-green-600 font-medium">(+)</td>
                  <td className="px-4 py-2 text-sm text-gray-900">
                    {r.conta}
                    {r.isParent && (
                      <span className="ml-1 text-xs text-indigo-600">({r.childCount} contas)</span>
                    )}
                  </td>
                  <td className="px-4 py-2 text-sm text-right text-gray-900">{formatCurrency(r.previsto)}</td>
                  <td className="px-4 py-2 text-sm text-right text-gray-900">{formatCurrency(r.realizado)}</td>
                  <td className={`px-4 py-2 text-sm text-right font-medium ${
                    r.diferenca > 0 ? 'text-green-600' : r.diferenca < 0 ? 'text-red-600' : 'text-gray-500'
                  }`}>
                    {r.diferenca > 0 && '+'}{formatCurrency(r.diferenca)}
                  </td>
                </tr>
              ))}
              <tr className="bg-green-50 font-semibold">
                <td className="px-4 py-3"></td>
                <td className="px-4 py-3 text-sm text-green-800">Total de Receitas</td>
                <td className="px-4 py-3 text-sm text-right text-green-800">{formatCurrency(comparativo.totalReceitasPrevisto)}</td>
                <td className="px-4 py-3 text-sm text-right text-green-800">{formatCurrency(comparativo.totalReceitasRealizado)}</td>
                <td className={`px-4 py-3 text-sm text-right font-bold ${
                  comparativo.totalReceitasRealizado - comparativo.totalReceitasPrevisto > 0
                    ? 'text-green-700'
                    : comparativo.totalReceitasRealizado - comparativo.totalReceitasPrevisto < 0
                    ? 'text-red-700'
                    : 'text-green-800'
                }`}>
                  {comparativo.totalReceitasRealizado - comparativo.totalReceitasPrevisto > 0 && '+'}
                  {formatCurrency(comparativo.totalReceitasRealizado - comparativo.totalReceitasPrevisto)}
                </td>
              </tr>
            </tbody>
          </table>
        </CardContent>
      </Card>

      {/* Despesas */}
      <Card className="border border-gray-200">
        <CardContent className="p-0">
          <div className="bg-red-50 px-4 py-3 border-b border-red-200">
            <h3 className="font-semibold text-red-800">Despesas - Previsto vs Realizado</h3>
          </div>
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-100">
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 w-10"></th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Conta</th>
                <th className="px-4 py-2 text-right text-xs font-medium text-gray-500">Previsto</th>
                <th className="px-4 py-2 text-right text-xs font-medium text-gray-500">Realizado</th>
                <th className="px-4 py-2 text-right text-xs font-medium text-gray-500">Diferença</th>
              </tr>
            </thead>
            <tbody>
              {comparativo.despesas.map((d) => (
                <tr key={d.codigo} className="border-b border-gray-50">
                  <td className="px-4 py-2 text-sm text-red-600 font-medium">(-)</td>
                  <td className="px-4 py-2 text-sm text-gray-900">
                    {d.conta}
                    {d.isParent && (
                      <span className="ml-1 text-xs text-indigo-600">({d.childCount} contas)</span>
                    )}
                  </td>
                  <td className="px-4 py-2 text-sm text-right text-gray-900">{formatCurrency(d.previsto)}</td>
                  <td className="px-4 py-2 text-sm text-right text-gray-900">{formatCurrency(d.realizado)}</td>
                  <td className={`px-4 py-2 text-sm text-right font-medium ${
                    d.diferenca > 0 ? 'text-red-600' : d.diferenca < 0 ? 'text-green-600' : 'text-gray-500'
                  }`}>
                    {d.diferenca > 0 && '+'}{formatCurrency(d.diferenca)}
                  </td>
                </tr>
              ))}
              <tr className="bg-red-50 font-semibold">
                <td className="px-4 py-3"></td>
                <td className="px-4 py-3 text-sm text-red-800">Total de Despesas</td>
                <td className="px-4 py-3 text-sm text-right text-red-800">{formatCurrency(comparativo.totalDespesasPrevisto)}</td>
                <td className="px-4 py-3 text-sm text-right text-red-800">{formatCurrency(comparativo.totalDespesasRealizado)}</td>
                <td className={`px-4 py-3 text-sm text-right font-bold ${
                  comparativo.totalDespesasRealizado - comparativo.totalDespesasPrevisto > 0
                    ? 'text-red-700'
                    : comparativo.totalDespesasRealizado - comparativo.totalDespesasPrevisto < 0
                    ? 'text-green-700'
                    : 'text-red-800'
                }`}>
                  {comparativo.totalDespesasRealizado - comparativo.totalDespesasPrevisto > 0 && '+'}
                  {formatCurrency(comparativo.totalDespesasRealizado - comparativo.totalDespesasPrevisto)}
                </td>
              </tr>
            </tbody>
          </table>
        </CardContent>
      </Card>

      {/* Resultado */}
      <Card className="border-2 border-gray-300">
        <CardContent className="p-5">
          <h3 className="font-semibold text-gray-900 mb-4">Resultado Comparativo</h3>
          <div className="space-y-3">
            <div className="flex items-center justify-between py-2 border-b border-gray-100">
              <span className="text-gray-600">Saldo Previsto</span>
              <span className={`font-semibold ${comparativo.saldoPrevisto >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {formatCurrency(comparativo.saldoPrevisto)}
              </span>
            </div>
            <div className="flex items-center justify-between py-2 border-b border-gray-100">
              <span className="text-gray-600">Saldo Realizado</span>
              <span className={`font-semibold ${comparativo.saldoRealizado >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {formatCurrency(comparativo.saldoRealizado)}
              </span>
            </div>
            <div className="flex items-center justify-between py-3">
              <span className="text-lg font-bold text-gray-900">Diferença</span>
              <span className={`text-2xl font-bold ${diferencaSaldo >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {diferencaSaldo > 0 && '+'}{formatCurrency(diferencaSaldo)}
              </span>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
